<!-- src/components/mailer/MailerWorkspace.vue -->
<template>
  <main class="container core-mailer-view">
    <MailerAccessState v-if="loadingAccess" title="Mailer" message="Checking access…" />

    <MailerAccessState
      v-else-if="!allowed"
      title="Mailer"
      message="This module is restricted to Operations and Social Media users."
      error
    />

    <MailerAccessState
      v-else-if="!effectiveVenueId"
      title="Mailer"
      message="Select a venue before opening the mailer."
    />

    <MailerStudio
      v-else
      :venue-id="effectiveVenueId"
      :is-operations="isOperationsUser"
      :is-social-media="isSocialMediaUser"
    />
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";

import { doc, getDoc } from "firebase/firestore";

import "@/assets/mailer-ui.css";

import MailerAccessState from "@/components/mailer/MailerAccessState.vue";
import MailerStudio from "@/components/mailer/MailerStudio.vue";

import { auth, db } from "@/firebase";

import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

const { venueId } = useVenueContext();

const loadingAccess = ref(true);

const allowed = ref(false);

const isOperationsUser = ref(false);

const isSocialMediaUser = ref(false);

const effectiveVenueId = computed(() => {
  return clean(venueId.value);
});

function clean(value) {
  return String(value ?? "").trim();
}

function cleanLower(value) {
  return clean(value).toLowerCase();
}

function asTrue(value) {
  if (value === true) {
    return true;
  }

  if (typeof value === "string") {
    return value.trim().toLowerCase() === "true";
  }

  return false;
}

function userIsOperations(claims = {}, profile = {}) {
  return (
    asTrue(claims.operations) ||
    asTrue(claims.ops) ||
    asTrue(claims.is_operations) ||
    asTrue(profile.isOperations) ||
    asTrue(profile.is_operations) ||
    asTrue(profile.operations) ||
    cleanLower(profile.user_type || profile.userType || profile.role) === "operations"
  );
}

function userIsSocialMedia(claims = {}, profile = {}) {
  const userType = cleanLower(profile.user_type || profile.userType || profile.role);

  return (
    asTrue(claims.social_media) ||
    asTrue(claims.socialMedia) ||
    asTrue(claims.is_social_media) ||
    asTrue(profile.isSocialMedia) ||
    asTrue(profile.is_social_media) ||
    asTrue(profile.social_media) ||
    userType === "social_media" ||
    userType === "social-media" ||
    userType === "social media"
  );
}

onMounted(async () => {
  try {
    const user = auth.currentUser;

    if (!user?.uid) {
      allowed.value = false;

      return;
    }

    const [tokenResult, userSnapshot] = await Promise.all([
      user.getIdTokenResult(),

      getDoc(doc(db, "users", user.uid)),
    ]);

    const claims = tokenResult?.claims || {};

    const profile = userSnapshot.exists() ? userSnapshot.data() || {} : {};

    isOperationsUser.value = userIsOperations(claims, profile);

    isSocialMediaUser.value = userIsSocialMedia(claims, profile);

    allowed.value = isOperationsUser.value || isSocialMediaUser.value;
  } catch (error) {
    console.error("[MailerWorkspace] access check failed", error);

    allowed.value = false;
  } finally {
    loadingAccess.value = false;
  }
});
</script>
